import { Injectable } from '@angular/core';

import { combineLatest, filter, map, pairwise, startWith, switchMap, tap, merge, share, Observable } from 'rxjs';

import { makeMap } from '@shared/core';
import { WebSocketEventsEnum } from '@shared/websocket';
import { UserAccountRole } from '@shared/user/enums';
import { AccountUser, LocationUser, User } from '@shared/user/models';
import { AuthUser } from '@shared/signup/models';
import { LocationApiService } from '../../../../services';
import { UserWithLocation } from '../models';
import {
  AccountApiService,
  AccountUsersQuery,
  AccountUsersStore,
  AuthRestaurantService,
  LocationUsersQuery,
  LocationUsersStore,
  SocketService,
  UsersQuery,
  UsersStore,
} from '@tablesready/frontend-store';

/**
 * Responsible for loading and synchronization data for location users/invites
 */
@Injectable()
export class LocationUserListService {
  private user: AuthUser = null;
  private allUsers: User[] = [];
  private admins: User[] = [];
  private locationUsers: UserWithLocation[] = [];
  private usersMap: Record<string, User> = {};
  private locationUsersMap: Record<string, LocationUser> = {};
  private accountUsersMap: Record<string, AccountUser> = {};

  constructor(
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly accountApiService: AccountApiService,
    private readonly locationApiService: LocationApiService,
    private readonly locationUsersStore: LocationUsersStore,
    private readonly usersStore: UsersStore,
    private readonly accountUserStore: AccountUsersStore,
    private readonly locationUsersQuery: LocationUsersQuery,
    private readonly usersQuery: UsersQuery,
    private readonly accountUserQuery: AccountUsersQuery,
    private readonly socketService: SocketService,
  ) {
  }

  load() {
    return this.authRestaurantService.selectWithAccount()
      .pipe(
        startWith(null),
        pairwise(),
        filter(([prev, current]) => prev?.location?._id !== current.location?._id),
        tap(([,user]) => this.user = user),
        switchMap(() => combineLatest([
          this.locationApiService.getLocationUsers(),
          this.accountApiService.getUsers(),
          this.accountApiService.getAccountUsers(),
        ])),
        tap(([locationUsers, users, accountUsers]) => {
          this.locationUsersStore.set(locationUsers);
          this.usersStore.set(users);
          this.accountUserStore.set(accountUsers);
        }),
      );
  }

  getData(): Observable<UserWithLocation[]> {
    return combineLatest([
      this.locationUsersQuery.selectAll()
        .pipe(tap(locationUsers => this.locationUsersMap = makeMap(locationUsers, 'userId'))),
      this.usersQuery.selectAll()
        .pipe(tap(users => {
          this.allUsers = users;
          this.usersMap = makeMap(users);
        })),
      this.accountUserQuery.selectAll()
        .pipe(tap(accountUsers => this.accountUsersMap = makeMap(accountUsers, 'userId'))),
    ])
      .pipe(
        map(([locationUsers]) => {
          const admins: User[] = [];
          const users = [];
          for (let i = 0; i < locationUsers.length; i++) {
            const user = this.usersMap[locationUsers[i].userId];
            if (!user) {
              continue;
            }

            if (this.accountUsersMap[locationUsers[i].userId]?.role === UserAccountRole.admin) {
              admins.push(user);
              continue;
            }

            const {name, email} = user;
            users.push({...locationUsers[i], name, email});
          }

          this.locationUsers = users;
          this.admins = admins;

          return users;
        }),
        share(),
      );
  }

  listenToChanges() {
    return merge(
      this.socketService.getEventStream<LocationUser[]>(WebSocketEventsEnum.locationUserCreated)
        .pipe(
          map(locationUsers => locationUsers.filter(locationsUser => locationsUser.locationId === this.user?.location?._id)),
          tap(locationUser => this.locationUsersStore.add(locationUser)),
        ),
      this.socketService.getEventStream<LocationUser[]>(WebSocketEventsEnum.locationUserUpdated)
        .pipe(
          map(locationUsers => locationUsers.filter(locationUser => locationUser.locationId === this.user?.location?._id)),
          tap(locationUsers => {
            for (let i = 0; i < locationUsers.length; i++) {
              this.locationUsersStore.update(locationUsers[i].id, locationUsers[i]);
            }
          }),
        ),
      this.socketService.getEventStream<string[]>(WebSocketEventsEnum.locationUserDeleted)
        .pipe(tap(ids => this.locationUsersStore.remove(ids))),
      this.socketService.getEventStream<AccountUser>(WebSocketEventsEnum.accountUserCreated)
        .pipe(
          filter(accountUser => accountUser.accountId === this.user?.account?.id),
          tap(accountUser => this.accountUserStore.add(accountUser)),
        ),
      this.socketService.getEventStream<AccountUser[]>(WebSocketEventsEnum.accountUserUpdated)
        .pipe(
          tap(accountUsers => {
            for (let i = 0; i < accountUsers.length; i++) {
              this.accountUserStore.update(accountUsers[i].id, accountUsers[i]);
            }
          }),
        ),
      this.socketService.getEventStream<string>(WebSocketEventsEnum.accountUserDeleted)
        .pipe(tap(id => this.accountUserStore.remove(id))),
      this.socketService.getEventStream<User>(WebSocketEventsEnum.userCreated)
        .pipe(tap(user => this.usersStore.add(user))),
      this.socketService.getEventStream<User>(WebSocketEventsEnum.userUpdated)
        .pipe(tap(user => this.usersStore.update(user._id, user))),
    );
  }

  getDataForInvite(): Observable<[string, User[], User[], User[], UserWithLocation[]]> {
    return this.getData().pipe(map(() => {
      const locationId = this.user?.location?._id;
      const usersToInvite = this.allUsers.filter(user => !!this.accountUsersMap[user._id] && !this.locationUsersMap[user._id]);
      const accountUsers = this.allUsers.filter(user => !!this.accountUsersMap[user._id]);

      return [locationId, usersToInvite, accountUsers, this.admins, this.locationUsers];
    }));
  }

  getUser(): AuthUser {
    return this.user;
  }

  reset() {
    this.locationUsersStore.reset();
    this.usersStore.reset();
    this.accountUserStore.reset();
    this.user = null;
    this.allUsers = [];
    this.admins = [];
  }
}
